import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Logo } from "@/components/Brand";

export const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-3 text-sm outline-none focus:border-[#0A192F] focus:ring-2 focus:ring-[#FFB703]/40";

const Shell = ({ title, sub, children }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="nf-navy nf-grain relative hidden flex-col justify-between p-12 lg:flex">
      <Link to="/"><Logo light /></Link>
      <div><p className="font-kn text-2xl text-[#FFB703]">ನಮ್ಮ ಫ್ಲೀಟ್</p><p className="mt-3 max-w-sm text-3xl font-semibold text-white">Rides with verified partners and upfront fares.</p></div>
      <div className="nf-road" />
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-md nf-rise">
        <Link to="/" className="mb-8 inline-block lg:hidden"><Logo /></Link>
        <h1 className="text-3xl font-bold tracking-tight text-[#0A192F]">{title}</h1>
        {sub && <p className="mt-2 text-sm text-slate-500">{sub}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const Btn = ({ busy, children, testid }) => (
  <button type="submit" disabled={busy} data-testid={testid} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0A192F] py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}
  </button>
);
const Err = ({ e }) => (e ? <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="auth-error">{e}</p> : null);

const GoogleBtn = () => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-login-btn" className="w-full rounded-xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
      Continue with Google (customers)
    </button>
  );
};

export function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState("");
  const submit = async (ev) => {
    ev.preventDefault(); setBusy(true); setE("");
    try {
      const { data } = await api.post("/auth/login", f);
      setUser(data);
      nav(sp.get("next") || homeFor(data), { replace: true });
    } catch (x) { setE(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" sub="Customers, driver partners and staff sign in here.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <input className={inputCls} type="email" placeholder="Email" value={f.email} onChange={(x) => setF({ ...f, email: x.target.value })} required data-testid="login-email-input" />
        <input className={inputCls} type="password" placeholder="Password" value={f.password} onChange={(x) => setF({ ...f, password: x.target.value })} required data-testid="login-password-input" />
        <Err e={e} />
        <Btn busy={busy} testid="login-submit-btn">Sign in</Btn>
      </form>
      <div className="my-5 text-center text-xs text-slate-400">or</div>
      <GoogleBtn />
      <div className="mt-6 flex justify-between text-sm">
        <Link to="/forgot-password" className="text-slate-600 hover:text-[#0A192F]" data-testid="forgot-link">Forgot password?</Link>
        <Link to={`/register${sp.get("next") ? `?next=${encodeURIComponent(sp.get("next"))}` : ""}`} className="font-semibold text-[#0A192F]" data-testid="register-link">Create account</Link>
      </div>
    </Shell>
  );
}

export function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", account_type: sp.get("type") === "driver" ? "driver" : "customer" });
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState("");
  const submit = async (ev) => {
    ev.preventDefault(); setBusy(true); setE("");
    try {
      const { data } = await api.post("/auth/register", f);
      setUser(data);
      nav(sp.get("next") || homeFor(data), { replace: true });
    } catch (x) { setE(errMsg(x)); } finally { setBusy(false); }
  };
  const s = (k) => (x) => setF({ ...f, [k]: x.target.value });
  return (
    <Shell title="Create your account" sub="Mobile OTP verification will be enabled once an SMS provider is configured.">
      <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
        {[["customer", "I'm a customer"], ["driver", "I'm a driver partner"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setF({ ...f, account_type: k })} data-testid={`register-type-${k}`}
            className={`rounded-lg py-2 text-sm font-semibold ${f.account_type === k ? "bg-[#0A192F] text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <input className={inputCls} placeholder="Full name" value={f.name} onChange={s("name")} required data-testid="register-name-input" />
        <input className={inputCls} placeholder="Mobile number (10 digits)" value={f.phone} onChange={s("phone")} required data-testid="register-phone-input" />
        <input className={inputCls} type="email" placeholder="Email" value={f.email} onChange={s("email")} required data-testid="register-email-input" />
        <input className={inputCls} type="password" placeholder="Password (min 8 characters)" minLength={8} value={f.password} onChange={s("password")} required data-testid="register-password-input" />
        <Err e={e} />
        <Btn busy={busy} testid="register-submit-btn">Create account</Btn>
      </form>
      <p className="mt-6 text-sm text-slate-600">Already registered? <Link to="/login" className="font-semibold text-[#0A192F]" data-testid="login-link">Sign in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/forgot-password", { email }); setMsg(data.message); } catch (x) { setMsg(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Reset your password" sub="We'll email you a link that expires in one hour.">
      <form onSubmit={submit} className="space-y-4">
        <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(x) => setEmail(x.target.value)} required data-testid="forgot-email-input" />
        {msg && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="forgot-message">{msg}</p>}
        <Btn busy={busy} testid="forgot-submit-btn">Send reset link</Btn>
      </form>
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [e, setE] = useState("");
  const submit = async (ev) => {
    ev.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); nav("/login"); } catch (x) { setE(errMsg(x)); }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <input className={inputCls} type="password" minLength={8} placeholder="New password" value={pw} onChange={(x) => setPw(x.target.value)} required data-testid="reset-password-input" />
        <Err e={e} />
        <Btn testid="reset-submit-btn">Update password</Btn>
      </form>
    </Shell>
  );
}

export function AuthCallback() {
  const loc = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data); window.history.replaceState(null, "", loc.pathname); nav(homeFor(data), { replace: true, state: { user: data } }); })
      .catch(() => nav("/login", { replace: true }));
  }, [loc, nav, setUser]);
  return null;
}
